import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, X, UserPlus } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'react-toastify';
import { useRegisterMutation } from '@/redux/api/auth';

const INDIAN_STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Andaman and Nicobar Islands','Chandigarh','Dadra and Nagar Haveli and Daman and Diu','Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry'];

const inputCls = "w-full h-10 px-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#1E40AF] focus:ring-2 focus:ring-[#1E40AF]/10 outline-none transition-all";

export default function SignUpModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [state, setState] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [register, { isLoading }] = useRegisterMutation();

  const reset = () => { setPhone(''); setOtp(''); setName(''); setPassword(''); setConfirmPassword(''); setState(''); setError(null); setIsOtpSent(false); setIsOtpVerified(false); };

  const handleRequestOtp = async (e) => {
    e.preventDefault(); setError(null); setIsOtpLoading(true);
    try {
      const res = await fetch('https://fastagtracking.com/qiktrack/requestOtp', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone }) });
      if (!res.ok) throw new Error('Failed to send OTP');
      const data = await res.json();
      if (data.success) { setIsOtpSent(true); toast.success('OTP sent!'); } else throw new Error(data.message || 'Failed');
    } catch (err) { setError(err.message); toast.error(err.message); } finally { setIsOtpLoading(false); }
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault(); setError(null);
    if (!otp || otp.length !== 4) { setError('Enter a valid 4-digit OTP'); return; }
    setIsOtpVerified(true);
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError(null);
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (!state) { setError('Please select a state'); return; }
    try {
      await register({ name, phone, password, otp, state }).unwrap();
      toast.success('Registration successful!'); reset(); onOpenChange(false); navigate('/');
    } catch (err) { const msg = err?.data?.message || 'Registration failed.'; setError(msg); toast.error(msg); }
  };

  const step = !isOtpSent ? 1 : !isOtpVerified ? 2 : 3;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-[420px] rounded-2xl p-0 gap-0 border-0 overflow-hidden">
        {/* Header */}
        <div className="bg-[#1B2A4A] px-6 pt-6 pb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <button onClick={() => onOpenChange(false)} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-white/70" />
            </button>
          </div>
          <h2 className="text-lg font-bold text-white">Create Account</h2>
          <p className="text-[13px] text-[#94A3B8] mt-0.5">
            {step === 1 && 'Get started with 10 free API hits'}
            {step === 2 && 'Verify your phone number'}
            {step === 3 && 'Complete your profile'}
          </p>
          {/* Step indicator */}
          <div className="flex gap-1.5 mt-4">
            {[1, 2, 3].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-all ${s <= step ? 'bg-[#3B82F6]' : 'bg-white/10'}`} />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {step === 1 && (
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div>
                <Label className="text-[12px] font-medium text-[#475569] mb-1.5 block">Phone Number</Label>
                <div className="flex gap-2">
                  <div className="h-10 px-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg flex items-center shrink-0">
                    <span className="text-[12px] font-semibold text-[#475569]">+91</span>
                  </div>
                  <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Enter phone number" required className={inputCls} />
                </div>
              </div>
              {error && <p className="text-[#DC2626] text-[12px] text-center bg-[#FEE2E2] rounded-lg px-3 py-2">{error}</p>}
              <Button type="submit" disabled={isOtpLoading || !phone} className="w-full h-10 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-lg text-[13px] font-semibold">
                {isOtpLoading ? 'Sending OTP...' : 'Get OTP'}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div>
                <Label className="text-[12px] font-medium text-[#475569] mb-2 block">Enter 4-digit OTP sent to {phone}</Label>
                <div className="flex justify-center">
                  <InputOTP maxLength={4} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      {[0, 1, 2, 3].map(i => <InputOTPSlot key={i} index={i} className="w-12 h-12 text-lg rounded-lg border-[#E2E8F0]" />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              {error && <p className="text-[#DC2626] text-[12px] text-center bg-[#FEE2E2] rounded-lg px-3 py-2">{error}</p>}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => { setIsOtpSent(false); setOtp(''); setError(null); }} className="flex-1 h-10 rounded-lg border-[#E2E8F0] text-[13px]">Back</Button>
                <Button type="submit" className="flex-1 h-10 bg-[#1E40AF] text-white rounded-lg text-[13px] font-semibold">Verify</Button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleRegister} className="space-y-3">
              <div>
                <Label className="text-[12px] font-medium text-[#475569] mb-1.5 block">Full Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required className={inputCls} />
              </div>
              <div>
                <Label className="text-[12px] font-medium text-[#475569] mb-1.5 block">State</Label>
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger className="h-10 rounded-lg border-[#E2E8F0] bg-[#F8FAFC] text-sm"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px] font-medium text-[#475569] mb-1.5 block">Password</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required className={inputCls} />
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]">{showPassword ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium text-[#475569] mb-1.5 block">Confirm Password</Label>
                <Input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat password" required className={inputCls} />
              </div>
              {error && <p className="text-[#DC2626] text-[12px] text-center bg-[#FEE2E2] rounded-lg px-3 py-2">{error}</p>}
              <Button type="submit" disabled={isLoading} className="w-full h-10 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-lg text-[13px] font-semibold mt-1">
                {isLoading ? 'Creating account...' : 'Create Account'}{!isLoading && <ArrowRight className="ml-1.5 h-4 w-4" />}
              </Button>
            </form>
          )}

          <p className="text-center text-[12px] text-[#94A3B8] mt-4">
            Already have an account?{' '}
            <button onClick={() => { onOpenChange(false); setTimeout(() => document.getElementById('login-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100); }} className="text-[#1E40AF] font-semibold hover:underline">Sign in</button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
